import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { FiFileText, FiDollarSign, FiCheckCircle, FiAlertCircle, FiCheck, FiClock } from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fileAPI } from '../utils/api';
import { getUser } from '../utils/auth';
import { CHART_COLORS } from '../utils/chartColors';
import './DashboardPage.css';

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(toNumber(value));

const formatCompactAmount = (value) => new Intl.NumberFormat('en-IN', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2
}).format(toNumber(value));

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = parseDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
};

const INITIAL_FORM = {
  insurerClaimNumber: '',
  statusPaidOutstanding: '',
  clientName: '',
  typeOfLoss: '',
  cwpReason: '',
  dateOfLoss: '',
  surveyorName: '',
  stateOfLoss: '',
  reportedAmount: '',
  assessmentFromSurveyor: '',
  paidAmount: '',
  osAmount: '',
  originalDocumentsStatus: '',
  remarks: '',
  pendingFrom: '',
  email: ''
};

const buildFormFromRow = (row = {}) => ({
  insurerClaimNumber: row.insurerClaimNumber || '',
  statusPaidOutstanding: row.statusPaidOutstanding || row.status || '',
  clientName: row.clientName || row.client || '',
  typeOfLoss: row.typeOfLoss || '',
  cwpReason: row.cwpReason || '',
  dateOfLoss: toDateInputValue(row.dateOfLoss),
  surveyorName: row.surveyorName || '',
  stateOfLoss: row.stateOfLoss || '',
  reportedAmount: row.reportedAmount ?? '',
  assessmentFromSurveyor: row.assessmentFromSurveyor || '',
  paidAmount: row.paidAmount ?? '',
  osAmount: row.osAmount ?? '',
  originalDocumentsStatus: row.originalDocumentsStatus || '',
  remarks: row.remarks || '',
  pendingFrom: row.pendingFrom || '',
  email: row.email || ''
});

const ClaimMisDashboardPage = ({ departmentId = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [deleteSubmittingId, setDeleteSubmittingId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormSubmitting, setAddFormSubmitting] = useState(false);
  const [addFormError, setAddFormError] = useState('');
  const [addFormSuccess, setAddFormSuccess] = useState('');
  const [addFormData, setAddFormData] = useState(INITIAL_FORM);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState(INITIAL_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  const isAdminRole = roleType === 'ADMIN';

  const analytics = useMemo(() => {
    const normalizedRows = rows.map((row) => {
      const reportedAmount = toNumber(row.reportedAmount);
      const paidAmount = toNumber(row.paidAmount);
      const osAmount = toNumber(row.osAmount);
      const status = String(row.statusPaidOutstanding || 'Unknown').trim() || 'Unknown';
      const client = String(row.clientName || 'Unknown').trim() || 'Unknown';
      return {
        ...row,
        reportedAmount,
        paidAmount,
        osAmount,
        status,
        client,
        lossDate: parseDate(row.dateOfLoss)
      };
    });

    const totalReported = normalizedRows.reduce((sum, row) => sum + row.reportedAmount, 0);
    const totalPaid = normalizedRows.reduce((sum, row) => sum + row.paidAmount, 0);
    const totalOutstanding = normalizedRows.reduce((sum, row) => sum + row.osAmount, 0);
    const settledCount = normalizedRows.filter((row) => row.status.toLowerCase().includes('settled')).length;
    const pendingCount = normalizedRows.length - settledCount;

    const byStatusMap = {};
    const byClientOutstandingMap = {};
    normalizedRows.forEach((row) => {
      byStatusMap[row.status] = (byStatusMap[row.status] || 0) + 1;
      byClientOutstandingMap[row.client] = (byClientOutstandingMap[row.client] || 0) + row.osAmount;
    });

    const byStatusData = Object.entries(byStatusMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const byClientOutstandingData = Object.entries(byClientOutstandingMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const clientOptions = [...new Set(normalizedRows.map((row) => row.client))].sort((a, b) => a.localeCompare(b));
    const statusOptions = [...new Set(normalizedRows.map((row) => row.status))].sort((a, b) => a.localeCompare(b));

    return {
      normalizedRows,
      totalReported,
      totalPaid,
      totalOutstanding,
      settledCount,
      pendingCount,
      byStatusData,
      byClientOutstandingData,
      clientOptions,
      statusOptions
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return analytics.normalizedRows.filter((row) => {
      if (clientFilter !== 'all' && row.client !== clientFilter) return false;
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (!term) return true;
      return [
        row.insurerClaimNumber,
        row.clientName,
        row.statusPaidOutstanding,
        row.typeOfLoss,
        row.surveyorName,
        row.stateOfLoss,
        row.remarks
      ].join(' ').toLowerCase().includes(term);
    });
  }, [analytics.normalizedRows, clientFilter, statusFilter, searchTerm]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRows.length / pageSize)), [filteredRows.length, pageSize]);
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPageSafe, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientFilter, statusFilter, searchTerm, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const fetchClaimMisRows = useCallback(async () => {
    if (!departmentId) {
      setError('Department is required to load Claim MIS List.');
      setCanSeePage(true);
      setRows([]);
      return;
    }

    const response = await fileAPI.getClaimMisList({ departmentId });
    const nextRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
    setRows(nextRows);
    setCanSeePage(true);
  }, [departmentId]);

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const openEditRow = (row) => {
    if (!isAdminRole || !row?.id) return;
    setEditingRow(row);
    setEditFormData(buildFormFromRow(row));
    setEditError('');
    setEditSuccess('');
    setDeleteError('');
    setDeleteSuccess('');
    setAddFormError('');
    setAddFormSuccess('');
  };

  const handleUpdateRow = async (event) => {
    event.preventDefault();
    if (!isAdminRole || !editingRow?.id) return;
    setEditError('');
    setEditSuccess('');
    try {
      setEditSubmitting(true);
      await fileAPI.updateClaimMisRow(editingRow.id, editFormData);
      setEditSuccess('Claim MIS row updated successfully.');
      await fetchClaimMisRows();
      setEditingRow(null);
    } catch (err) {
      setEditError(err?.message || 'Failed to update Claim MIS row.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCreateRow = async (event) => {
    event.preventDefault();
    setAddFormError('');
    setAddFormSuccess('');
    setDeleteError('');
    setDeleteSuccess('');

    if (!departmentId) {
      setAddFormError('Department context is required.');
      return;
    }

    try {
      setAddFormSubmitting(true);
      await fileAPI.createClaimMisRow({
        departmentId,
        ...addFormData
      });
      setAddFormSuccess('Claim MIS entry added successfully.');
      setAddFormData(INITIAL_FORM);
      await fetchClaimMisRows();
    } catch (err) {
      setAddFormError(err?.message || 'Failed to add Claim MIS entry.');
    } finally {
      setAddFormSubmitting(false);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!isAdminRole) return;
    const id = row?.id;
    if (!id) return;

    setDeleteError('');
    setDeleteSuccess('');
    setAddFormError('');
    setAddFormSuccess('');

    const label = row?.insurerClaimNumber || row?.clientName || `ID ${id}`;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Delete this Claim MIS row (${label})? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeleteSubmittingId(id);
      await fileAPI.deleteClaimMisRow(id);
      setDeleteSuccess('Claim MIS row deleted successfully.');
      await fetchClaimMisRows();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete Claim MIS row.');
    } finally {
      setDeleteSubmittingId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        await fetchClaimMisRows();
      } catch (err) {
        const message = err?.message || 'Failed to load Claim MIS List.';
        if (message.includes('403')) setCanSeePage(false);
        else setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchClaimMisRows]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading Claim MIS...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      {error && <Alert variant="danger">{error}</Alert>}
      {deleteError && <Alert variant="danger">{deleteError}</Alert>}
      {deleteSuccess && <Alert variant="success">{deleteSuccess}</Alert>}
      {editSuccess && <Alert variant="success">{editSuccess}</Alert>}
      {!canSeePage ? (
        <Alert variant="warning">Claim MIS is available only for Finance users and EXCO.</Alert>
      ) : (
        <>
          <Row className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-6">
            <Col>
              <Card className="stat-card stat-primary h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiFileText /></div>
                  <div>
                    <p className="stat-title mb-1">Total Claims</p>
                    <h5 className="mb-0 fw-bold">{analytics.normalizedRows.length}</h5>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="stat-card stat-info h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiDollarSign /></div>
                  <div>
                    <p className="stat-title mb-1">Reported Amount</p>
                    <h5 className="mb-0 fw-bold">{formatAmount(analytics.totalReported)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="stat-card stat-success h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCheckCircle /></div>
                  <div>
                    <p className="stat-title mb-1">Paid Amount</p>
                    <h5 className="mb-0 fw-bold">{formatAmount(analytics.totalPaid)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="stat-card stat-warning h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiAlertCircle /></div>
                  <div>
                    <p className="stat-title mb-1">Outstanding Amount</p>
                    <h5 className="mb-0 fw-bold">{formatAmount(analytics.totalOutstanding)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="stat-card stat-success h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCheck /></div>
                  <div>
                    <p className="stat-title mb-1">Settled</p>
                    <Badge bg="success">{analytics.settledCount}</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="stat-card stat-warning h-100">
                <Card.Body className="d-flex align-items-center gap-2">
                  <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiClock /></div>
                  <div>
                    <p className="stat-title mb-1">Pending</p>
                    <Badge bg="warning" text="dark">{analytics.pendingCount}</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col lg={6}>
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Claims by Status</h6></Card.Header>
                <Card.Body style={{ minHeight: 320 }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={analytics.byStatusData} dataKey="value" nameKey="name" outerRadius={100}>
                        {analytics.byStatusData.map((entry, index) => <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Outstanding by Client</h6></Card.Header>
                <Card.Body style={{ minHeight: 320 }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.byClientOutstandingData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={formatCompactAmount} />
                      <Tooltip formatter={(value) => formatAmount(value)} />
                      <Bar dataKey="value" fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-3">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Add Claim MIS Entry</h5>
              <Button size="sm" variant={showAddForm ? 'outline-secondary' : 'primary'} onClick={() => { setShowAddForm((prev) => !prev); setAddFormError(''); setAddFormSuccess(''); }}>
                {showAddForm ? 'Hide Form' : 'Add Entry'}
              </Button>
            </Card.Header>
            {showAddForm && (
              <Card.Body>
                {addFormError && <Alert variant="danger" className="mb-3">{addFormError}</Alert>}
                {addFormSuccess && <Alert variant="success" className="mb-3">{addFormSuccess}</Alert>}
                <Form onSubmit={handleCreateRow}>
                  <Row className="g-3">
                    <Col md={4}><Form.Label className="mb-1">Insurer Claim Number</Form.Label><Form.Control value={addFormData.insurerClaimNumber} onChange={(e) => handleAddFormChange('insurerClaimNumber', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Status</Form.Label><Form.Control value={addFormData.statusPaidOutstanding} onChange={(e) => handleAddFormChange('statusPaidOutstanding', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Client Name</Form.Label><Form.Control value={addFormData.clientName} onChange={(e) => handleAddFormChange('clientName', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Type of Loss</Form.Label><Form.Control value={addFormData.typeOfLoss} onChange={(e) => handleAddFormChange('typeOfLoss', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">CWP Reason</Form.Label><Form.Control value={addFormData.cwpReason} onChange={(e) => handleAddFormChange('cwpReason', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Date of Loss</Form.Label><Form.Control type="date" value={addFormData.dateOfLoss} onChange={(e) => handleAddFormChange('dateOfLoss', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Surveyor Name</Form.Label><Form.Control value={addFormData.surveyorName} onChange={(e) => handleAddFormChange('surveyorName', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">State of Loss</Form.Label><Form.Control value={addFormData.stateOfLoss} onChange={(e) => handleAddFormChange('stateOfLoss', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Reported Amount</Form.Label><Form.Control type="number" step="0.01" value={addFormData.reportedAmount} onChange={(e) => handleAddFormChange('reportedAmount', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Assessment from Surveyor</Form.Label><Form.Control value={addFormData.assessmentFromSurveyor} onChange={(e) => handleAddFormChange('assessmentFromSurveyor', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Paid Amount</Form.Label><Form.Control type="number" step="0.01" value={addFormData.paidAmount} onChange={(e) => handleAddFormChange('paidAmount', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">O/S Amount</Form.Label><Form.Control type="number" step="0.01" value={addFormData.osAmount} onChange={(e) => handleAddFormChange('osAmount', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Original Documents Status</Form.Label><Form.Control value={addFormData.originalDocumentsStatus} onChange={(e) => handleAddFormChange('originalDocumentsStatus', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Pending From</Form.Label><Form.Control value={addFormData.pendingFrom} onChange={(e) => handleAddFormChange('pendingFrom', e.target.value)} /></Col>
                    <Col md={4}><Form.Label className="mb-1">Email</Form.Label><Form.Control type="email" value={addFormData.email} onChange={(e) => handleAddFormChange('email', e.target.value)} /></Col>
                    <Col md={12}><Form.Label className="mb-1">Remarks</Form.Label><Form.Control value={addFormData.remarks} onChange={(e) => handleAddFormChange('remarks', e.target.value)} /></Col>
                  </Row>
                  <div className="mt-3 d-flex justify-content-end">
                    <Button type="submit" disabled={addFormSubmitting}>{addFormSubmitting ? 'Saving...' : 'Save Entry'}</Button>
                  </div>
                </Form>
              </Card.Body>
            )}
          </Card>

          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom"><h5 className="mb-0">Claim MIS Table</h5></Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={3}><Form.Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}><option value="all">All Clients</option>{analytics.clientOptions.map((item) => <option key={item} value={item}>{item}</option>)}</Form.Select></Col>
                <Col md={3}><Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="all">All Status</option>{analytics.statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}</Form.Select></Col>
                <Col md={6}><Form.Control value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search claim no / client / status / loss / remarks" /></Col>
              </Row>

              {!filteredRows.length ? (
                <p className="text-muted mb-0">No Claim MIS records found for applied filters.</p>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Sr No</th>
                        <th>Insurer Claim Number</th>
                        <th>Status</th>
                        <th>Client</th>
                        <th>Type of Loss</th>
                        <th>Date of Loss</th>
                        <th>Surveyor</th>
                        <th>State</th>
                        <th>Reported</th>
                        <th>Paid</th>
                        <th>O/S</th>
                        <th>Docs Status</th>
                        <th>Pending From</th>
                        {isAdminRole ? <th>Actions</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, index) => (
                        <tr key={`${row.id}-${index}`}>
                          <td>{row.srNo || '-'}</td>
                          <td>{row.insurerClaimNumber || '-'}</td>
                          <td>{row.status || '-'}</td>
                          <td>{row.client || '-'}</td>
                          <td>{row.typeOfLoss || '-'}</td>
                          <td>{formatDate(row.dateOfLoss)}</td>
                          <td>{row.surveyorName || '-'}</td>
                          <td>{row.stateOfLoss || '-'}</td>
                          <td>{formatAmount(row.reportedAmount)}</td>
                          <td>{formatAmount(row.paidAmount)}</td>
                          <td>{formatAmount(row.osAmount)}</td>
                          <td>{row.originalDocumentsStatus || '-'}</td>
                          <td>{formatDate(row.pendingFrom)}</td>
                          {isAdminRole ? (
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2"
                                onClick={() => openEditRow(row)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={deleteSubmittingId === row.id}
                                onClick={() => handleDeleteRow(row)}
                              >
                                {deleteSubmittingId === row.id ? 'Deleting…' : 'Delete'}
                              </Button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <small className="text-muted">Showing {(currentPageSafe - 1) * pageSize + 1}-{Math.min(currentPageSafe * pageSize, filteredRows.length)} of {filteredRows.length}</small>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">Rows per page</small>
                        <Form.Select size="sm" style={{ width: 90 }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </Form.Select>
                      </div>
                    </div>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPageSafe <= 1} />
                      <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPageSafe <= 1} />
                      <Pagination.Item active>{currentPageSafe}</Pagination.Item>
                      <Pagination.Item disabled>{`/ ${totalPages}`}</Pagination.Item>
                      <Pagination.Next onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPageSafe >= totalPages} />
                      <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPageSafe >= totalPages} />
                    </Pagination>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Modal show={Boolean(editingRow)} onHide={() => setEditingRow(null)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>Edit Claim MIS Row</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleUpdateRow}>
              <Modal.Body>
                {editError && <Alert variant="danger">{editError}</Alert>}
                <Row className="g-3">
                  <Col md={4}><Form.Label className="mb-1">Insurer Claim Number</Form.Label><Form.Control value={editFormData.insurerClaimNumber} onChange={(e) => handleEditFormChange('insurerClaimNumber', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Status</Form.Label><Form.Control value={editFormData.statusPaidOutstanding} onChange={(e) => handleEditFormChange('statusPaidOutstanding', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Client Name</Form.Label><Form.Control value={editFormData.clientName} onChange={(e) => handleEditFormChange('clientName', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Type of Loss</Form.Label><Form.Control value={editFormData.typeOfLoss} onChange={(e) => handleEditFormChange('typeOfLoss', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">CWP Reason</Form.Label><Form.Control value={editFormData.cwpReason} onChange={(e) => handleEditFormChange('cwpReason', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Date of Loss</Form.Label><Form.Control type="date" value={editFormData.dateOfLoss} onChange={(e) => handleEditFormChange('dateOfLoss', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Surveyor Name</Form.Label><Form.Control value={editFormData.surveyorName} onChange={(e) => handleEditFormChange('surveyorName', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">State of Loss</Form.Label><Form.Control value={editFormData.stateOfLoss} onChange={(e) => handleEditFormChange('stateOfLoss', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Reported Amount</Form.Label><Form.Control type="number" step="0.01" value={editFormData.reportedAmount} onChange={(e) => handleEditFormChange('reportedAmount', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Assessment from Surveyor</Form.Label><Form.Control value={editFormData.assessmentFromSurveyor} onChange={(e) => handleEditFormChange('assessmentFromSurveyor', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Paid Amount</Form.Label><Form.Control type="number" step="0.01" value={editFormData.paidAmount} onChange={(e) => handleEditFormChange('paidAmount', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">O/S Amount</Form.Label><Form.Control type="number" step="0.01" value={editFormData.osAmount} onChange={(e) => handleEditFormChange('osAmount', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Original Documents Status</Form.Label><Form.Control value={editFormData.originalDocumentsStatus} onChange={(e) => handleEditFormChange('originalDocumentsStatus', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Pending From</Form.Label><Form.Control value={editFormData.pendingFrom} onChange={(e) => handleEditFormChange('pendingFrom', e.target.value)} /></Col>
                  <Col md={4}><Form.Label className="mb-1">Email</Form.Label><Form.Control type="email" value={editFormData.email} onChange={(e) => handleEditFormChange('email', e.target.value)} /></Col>
                  <Col md={12}><Form.Label className="mb-1">Remarks</Form.Label><Form.Control value={editFormData.remarks} onChange={(e) => handleEditFormChange('remarks', e.target.value)} /></Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setEditingRow(null)}>Cancel</Button>
                <Button type="submit" disabled={editSubmitting}>{editSubmitting ? 'Saving...' : 'Save Changes'}</Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default ClaimMisDashboardPage;
